/**
 * MasterClassViewModal - Модальное окно просмотра мастер-класса
 */
import React from 'react';
import {
  Modal,
  Text,
  Group,
  Stack,
  Box,
  Badge,
  Divider,
  Button,
  Paper,
} from '@mantine/core';
import {
  IconCalendarEvent,
  IconUser,
  IconUsers,
  IconClock,
  IconSchool,
  IconPencil,
  IconClipboardCheck,
  IconX,
  IconCheck,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

/**
 * Форматирование даты и времени
 */
const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

/**
 * Форматирование длительности
 */
const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) {
    return `${hours}ч ${mins}мин`;
  } else if (hours > 0) {
    return `${hours}ч`;
  } else {
    return `${mins}мин`;
  }
};

/**
 * Получить цвет статуса
 */
const getStatusColor = (status) => {
  switch (status) {
    case 'planned':
      return 'yellow';
    case 'completed':
      return 'green';
    case 'cancelled':
      return 'red';
    default:
      return 'gray';
  }
};

/**
 * Получить название статуса
 */
const getStatusLabel = (status) => {
  switch (status) {
    case 'planned':
      return 'Запланировано';
    case 'completed':
      return 'Проведено';
    case 'cancelled':
      return 'Отменено';
    default:
      return status;
  }
};

const MasterClassViewModal = ({
  opened,
  onClose,
  masterClass,
  onEdit,
  onResult,
  onCancel,
  isAdmin = false,
}) => {
  if (!masterClass) return null;

  const isPlanned = masterClass.status === 'planned';
  const now = new Date();
  const scheduledAt = new Date(masterClass.scheduled_at);
  const canAddResult = isPlanned && (isAdmin || scheduledAt <= now);

  // Считаем общее количество детей
  const totalChildren = masterClass.groups?.reduce((sum, g) => sum + (g.children_count || 0), 0) || 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Мастер-класс"
      size="lg"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <Stack gap="md">
        {/* Статусы и бейджи */}
        <Group gap="xs">
          <Badge
            size="md"
            color={getStatusColor(masterClass.status)}
            variant="filled"
            radius="xl"
            leftSection={
              masterClass.status === 'completed' ? <IconCheck size={12} /> :
              masterClass.status === 'cancelled' ? <IconX size={12} /> : null
            }
          >
            {getStatusLabel(masterClass.status)}
          </Badge>

          {masterClass.reschedule_count > 0 && (
            <Badge size="md" color="orange" variant="light" radius="xl">
              Перенесено {masterClass.reschedule_count}
            </Badge>
          )}

          {masterClass.edit_count > 0 && (
            <Badge size="md" color="blue" variant="light" radius="xl">
              Изменено {masterClass.edit_count}
            </Badge>
          )}
        </Group>

        {/* Дата и время */}
        <Group gap="sm">
          <IconCalendarEvent size={20} color={tokens.colors.neutral[6]} />
          <Text size="lg" fw={500}>
            {formatDateTime(masterClass.scheduled_at)}
          </Text>
        </Group>

        <Divider
          label={
            <Group gap="xs">
              <Text size="sm" c="dimmed">Группы ({masterClass.total_groups})</Text>
              <Text size="sm" c="dimmed">•</Text>
              <Text size="sm" c="dimmed">Всего детей: {totalChildren}</Text>
            </Group>
          }
          labelPosition="left"
        />

        {/* Группы */}
        <Stack gap="sm">
          {masterClass.groups?.map((group, index) => (
            <Paper
              key={group.id || index}
              p="sm"
              withBorder
              radius="xl"
              style={{ backgroundColor: tokens.colors.neutral[1] }}
            >
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <Text fw={500} size="sm">{group.name}</Text>
                  <Text size="sm" c="dimmed">•</Text>
                  <Text size="sm" c="blue" fw={500}>
                    {group.subject_name || 'Без предмета'}
                  </Text>
                </Group>
              </Group>

              <Group gap="lg">
                <Group gap={4}>
                  <IconUsers size={14} color={tokens.colors.neutral[6]} />
                  <Text size="sm" c="dimmed">
                    {group.children_count || 0} детей
                  </Text>
                </Group>

                <Group gap={4}>
                  <IconClock size={14} color={tokens.colors.neutral[6]} />
                  <Text size="sm" c="dimmed">
                    {formatDuration(group.duration_minutes)}
                  </Text>
                </Group>

                {group.teacher_name && (
                  <Group gap={4}>
                    <IconSchool size={14} color={tokens.colors.neutral[6]} />
                    <Text size="sm" c="dimmed">
                      {group.teacher_name}
                    </Text>
                  </Group>
                )}
              </Group>
            </Paper>
          ))}
        </Stack>

        <Divider label="Ответственные" labelPosition="left" />

        {/* Ответственные */}
        <Group grow>
          <Box>
            <Text size="xs" c="dimmed" mb={4}>Менеджер</Text>
            <Group gap={4}>
              <IconUser size={14} color={tokens.colors.neutral[6]} />
              <Text size="sm">{masterClass.manager_name || '—'}</Text>
            </Group>
          </Box>
          <Box>
            <Text size="xs" c="dimmed" mb={4}>Сейл</Text>
            <Group gap={4}>
              <IconUser size={14} color={tokens.colors.neutral[6]} />
              <Text size="sm">{masterClass.sales_name || '—'}</Text>
            </Group>
          </Box>
        </Group>

        {/* Комментарий */}
        {masterClass.comment && (
          <>
            <Divider label="Комментарий" labelPosition="left" />
            <Text size="sm" c="dimmed">
              {masterClass.comment}
            </Text>
          </>
        )}

        {/* Результат МК */}
        {masterClass.result_comment && (
          <>
            <Divider />
            <Stack gap="sm">
              <Group gap="xs">
                <IconClipboardCheck size={14} color={tokens.colors.semantic.success.primary} />
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Результат МК
                </Text>
                {masterClass.completed_at && (
                  <Text size="xs" c="dimmed" ml="auto">
                    {new Date(masterClass.completed_at).toLocaleDateString('ru-RU', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      timeZone: 'Europe/Moscow',
                    })}
                  </Text>
                )}
              </Group>
              <Box
                p="md"
                style={{
                  backgroundColor: '#E8F5E9',
                  borderRadius: '12px',
                  borderLeft: `3px solid ${tokens.colors.semantic.success.primary}`,
                }}
              >
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {masterClass.result_comment}
                </Text>
              </Box>
            </Stack>
          </>
        )}

        {/* Причина отмены */}
        {masterClass.cancellation_reason && (
          <>
            <Divider />
            <Stack gap="sm">
              <Group gap="xs">
                <IconX size={14} color={tokens.colors.semantic.error.primary} />
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Причина отмены
                </Text>
                {masterClass.cancelled_at && (
                  <Text size="xs" c="dimmed" ml="auto">
                    {new Date(masterClass.cancelled_at).toLocaleDateString('ru-RU', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      timeZone: 'Europe/Moscow',
                    })}
                  </Text>
                )}
              </Group>
              <Box
                p="md"
                style={{
                  backgroundColor: '#FFEBEE',
                  borderRadius: '12px',
                  borderLeft: `3px solid ${tokens.colors.semantic.error.primary}`,
                }}
              >
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {masterClass.cancellation_reason}
                </Text>
              </Box>
            </Stack>
          </>
        )}

        {/* Кнопки действий */}
        <Divider />

        <Group justify="space-between">
          <Group gap="xs">
            {isPlanned && (
              <>
                <Button
                  variant="light"
                  leftSection={<IconPencil size={14} />}
                  onClick={() => onEdit(masterClass)}
                  size="sm"
                >
                  Редактировать
                </Button>

                {canAddResult && (
                  <Button
                    variant="light"
                    color="green"
                    leftSection={<IconClipboardCheck size={14} />}
                    onClick={() => onResult(masterClass)}
                    size="sm"
                  >
                    Результат МК
                  </Button>
                )}

                <Button
                  variant="light"
                  color="orange"
                  leftSection={<IconX size={14} />}
                  onClick={() => onCancel(masterClass)}
                  size="sm"
                >
                  Отменить МК
                </Button>
              </>
            )}
          </Group>

          <Button variant="subtle" onClick={onClose}>
            Закрыть
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default MasterClassViewModal;
